import type { Instance } from "mobx-state-tree";
import { types, flow } from "mobx-state-tree";
import * as pmRepo from "./repository";
import { getUnreadDocumentMentionCount } from "@/stores/documents/comments/repository";

/**
 * MentionsStore (WHA-3339) — drives the red unread-mention dot on the
 * Project Management nav icon. Scoped to the currently-viewed building;
 * BuildingLayout calls setContext + refresh on mount, and refresh()
 * again after window 'franklin:pm-issue-changed' events the chatbot
 * dispatches when it writes a comment.
 *
 * `documentUnreadCount` is the same idea for @mentions in document comment
 * threads (the Documents nav dot). It is refreshed by the comments realtime
 * bridge and after a thread marks its mentions read
 * ('franklin:document-mentions-read').
 */
export const MentionsStore = types
  .model("MentionsStore", {
    unreadCount: types.optional(types.number, 0),
    documentUnreadCount: types.optional(types.number, 0),
  })
  .volatile(() => ({
    buildingId: 0 as number,
    currentUserId: 0 as number,
    projectId: null as number | null,
  }))
  .actions((self) => ({
    setContext(buildingId: number, currentUserId: number, projectId?: number | null) {
      self.buildingId = buildingId;
      self.currentUserId = currentUserId;
      self.projectId = projectId ?? null;
    },
    refresh: flow(function* refresh() {
      if (!self.buildingId || !self.currentUserId) return;
      try {
        self.unreadCount = yield pmRepo.getUnreadMentionCountForBuilding(
          self.buildingId,
          self.currentUserId,
          self.projectId
        );
      } catch {
        // Best-effort: the dot just stays at its previous value. Don't
        // blow up the layout because of a notification query.
      }
    }),
    refreshDocuments: flow(function* refreshDocuments() {
      if (!self.buildingId || !self.currentUserId) return;
      try {
        self.documentUnreadCount = yield getUnreadDocumentMentionCount(self.buildingId);
      } catch {
        // Best-effort, like refresh(): the dot keeps its previous value.
      }
    }),
    decrement(by: number) {
      self.unreadCount = Math.max(0, self.unreadCount - by);
    },
  }));

export type MentionsStoreInstance = Instance<typeof MentionsStore>;
